import React, { createContext, useContext, useState, useEffect } from 'react';

const AuthContext = createContext();
const API_URL = process.env.REACT_APP_API_URL;

// Helper function to adjust color lightness for lighter shade
function adjustColorLightness(hex, percent) {
    // Remove # if present
    hex = hex.replace('#', '');
    
    // Convert to RGB
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    
    // Convert to HSL
    const rNorm = r / 255;
    const gNorm = g / 255;
    const bNorm = b / 255;
    
    const max = Math.max(rNorm, gNorm, bNorm);
    const min = Math.min(rNorm, gNorm, bNorm);
    let h, s, l = (max + min) / 2;
    
    if (max === min) {
        h = s = 0;
    } else {
        const d = max - min;
        s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
        
        switch (max) {
            case rNorm: h = ((gNorm - bNorm) / d + (gNorm < bNorm ? 6 : 0)) / 6; break;
            case gNorm: h = ((bNorm - rNorm) / d + 2) / 6; break;
            case bNorm: h = ((rNorm - gNorm) / d + 4) / 6; break;
            default: h = 0;
        }
    }
    
    // Adjust lightness
    l = Math.min(1, Math.max(0, l + percent / 100));
    
    // Convert back to RGB
    let r2, g2, b2;
    if (s === 0) {
        r2 = g2 = b2 = l;
    } else {
        const hue2rgb = (p, q, t) => {
            if (t < 0) t += 1;
            if (t > 1) t -= 1;
            if (t < 1/6) return p + (q - p) * 6 * t;
            if (t < 1/2) return q;
            if (t < 2/3) return p + (q - p) * (2/3 - t) * 6;
            return p;
        };
        
        const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
        const p = 2 * l - q;
        r2 = hue2rgb(p, q, h + 1/3);
        g2 = hue2rgb(p, q, h);
        b2 = hue2rgb(p, q, h - 1/3);
    }
    
    // Convert to hex
    const toHex = (val) => {
        const hex = Math.round(val * 255).toString(16);
        return hex.length === 1 ? '0' + hex : hex;
    };
    
    return `#${toHex(r2)}${toHex(g2)}${toHex(b2)}`;
}

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

export const AuthProvider = ({ children }) => {
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [user, setUser] = useState(null);

    // Function to fetch and apply user feature flags
    const fetchAndApplyUserFeatureFlags = async (userId) => {
        try {
            const response = await fetch(`${API_URL}/api/feature-flags/user/${userId}`);
            if (response.ok) {
                const flags = await response.json();
                
                // Apply darkTheme if enabled
                if (flags.darkTheme) {
                    const isDarkTheme = flags.darkTheme.enabled;
                    localStorage.setItem('darkTheme', isDarkTheme.toString());
                    
                    if (isDarkTheme) {
                        document.body.classList.add('dark-theme');
                    } else {
                        document.body.classList.remove('dark-theme');
                    }
                }
                
                // Apply custom theme color if enabled
                if (flags.themeColor) {
                    const customColor = flags.themeColor.value || '#0d6efd';
                    
                    if (flags.themeColor.enabled) {
                        document.documentElement.style.setProperty('--theme-primary', customColor);
                        
                        // Calculate lighter shade for hover/borders
                        const lighterColor = adjustColorLightness(customColor, 20);
                        document.documentElement.style.setProperty('--theme-primary-light', lighterColor);
                    } else {
                        // Reset to default blue when disabled
                        document.documentElement.style.setProperty('--theme-primary', '#0d6efd');
                        document.documentElement.style.setProperty('--theme-primary-light', '#6ea8fe');
                    }
                }
                
                // Store feature flags in localStorage for quick access
                localStorage.setItem('userFeatureFlags', JSON.stringify(flags));
            }
        } catch (error) {
            console.error('Error fetching user feature flags:', error);
        }
    };

    useEffect(() => {
        // Check if user is already logged in (from localStorage)
        const authStatus = localStorage.getItem('isAuthenticated');
        const userData = localStorage.getItem('user');
        
        if (authStatus === 'true' && userData) {
            const parsedUser = JSON.parse(userData);
            setIsAuthenticated(true);
            setUser(parsedUser);
            
            // Fetch and apply user feature flags on mount if logged in
            if (parsedUser && parsedUser.id) {
                fetchAndApplyUserFeatureFlags(parsedUser.id);
            }
        }
        setIsLoading(false);
    }, []);

    const login = async (userData = null) => {
        setIsAuthenticated(true);
        localStorage.setItem('isAuthenticated', 'true');
        
        if (userData) {
            setUser(userData);
            localStorage.setItem('user', JSON.stringify(userData));
            
            // Fetch and apply user feature flags immediately on login
            if (userData.id) {
                await fetchAndApplyUserFeatureFlags(userData.id);
            }
        }
    };

    const logout = () => {
        setIsAuthenticated(false);
        setUser(null);
        localStorage.removeItem('isAuthenticated');
        localStorage.removeItem('user');
        localStorage.removeItem('userFeatureFlags');
        localStorage.removeItem('darkTheme');
        
        // Remove dark theme on logout
        document.body.classList.remove('dark-theme');
        
        // Reset theme color to default on logout
        document.documentElement.style.setProperty('--theme-primary', '#0d6efd');
        document.documentElement.style.setProperty('--theme-primary-light', '#6ea8fe');
    };

    // Function to refresh feature flags (can be called when flags are updated)
    const refreshFeatureFlags = async () => {
        if (user && user.id) {
            await fetchAndApplyUserFeatureFlags(user.id);
        }
    };

    const value = {
        isAuthenticated,
        user,
        login,
        logout,
        isLoading,
        refreshFeatureFlags
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};